import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { DepthLadder } from '../components/DepthLadder';
import { OrderTicket } from '../components/OrderTicket';
import { PriceChart } from '../components/PriceChart';
import { api } from '../lib/api';
import { useSession } from '../lib/useSession';
import { credits, payoutOf, relativeTime, tradeTime } from '../lib/format';
import { StatusBadge } from './Markets';

export function MarketPage({ signedIn }: { signedIn: boolean }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const token = useSession();

  const market = useQuery({ queryKey: ['market', id], queryFn: () => api.market(id), enabled: id !== '' });
  const trades = useQuery({ queryKey: ['trades', id], queryFn: () => api.trades(id), enabled: id !== '' });
  const positions = useQuery({ queryKey: ['positions'], queryFn: api.positions, enabled: token !== null });

  if (market.isLoading) return <div className="empty-state">Loading…</div>;
  if (market.isError || !market.data) return <div className="empty-state">No such market.</div>;

  const { market: m, book } = market.data;
  const mine = (positions.data ?? []).filter((p) => p.market_id === id);
  const tradeList = trades.data ?? [];

  /**
   * Everything a trade can change.
   *
   * `orders` and `summary` are in here for a reason worth keeping: signing in
   * lands on the dashboard, which fills the cache with an empty portfolio.
   * Trading and navigating back then rendered that stale empty state for a
   * moment before the refetch landed, so a user who had just placed an order
   * was told they had none.
   */
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['market', id] });
    void qc.invalidateQueries({ queryKey: ['trades', id] });
    void qc.invalidateQueries({ queryKey: ['positions'] });
    void qc.invalidateQueries({ queryKey: ['orders'] });
    void qc.invalidateQueries({ queryKey: ['summary'] });
    void qc.invalidateQueries({ queryKey: ['performance'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };

  const ticketBlocker = !signedIn
    ? 'Sign in to trade.'
    : m.status !== 'OPEN'
      ? 'This market is closed for trading.'
      : undefined;

  return (
    <>
      <Link to="/" className="muted" style={{ fontSize: 13 }}>← All markets</Link>

      <h1 className="question" style={{ marginTop: 12 }}>{m.question}</h1>
      <div className="meta">
        <StatusBadge status={m.status} />
        {m.status === 'OPEN' && <span>Closes {relativeTime(m.closes_at)}</span>}
        {m.outcome && <span>Resolved <b>{m.outcome}</b></span>}
        <span>{m.open_interest.toLocaleString()} contracts open</span>
      </div>

      <div className="grid market-layout">
        <div className="stack">
          <div className="card">
            <h2>YES price</h2>
            <div className="prob" style={{ marginBottom: 10 }}>
              <b>{m.last_price === null ? '—' : `${m.last_price}%`}</b>
              <small>implied chance of YES</small>
            </div>
            <PriceChart trades={tradeList} />
          </div>

          <div className="card">
            <h2>Order book</h2>
            <DepthLadder book={book} />
          </div>

          {mine.length > 0 && (
            <div className="card">
              <h2>Your position</h2>
              <table className="data">
                <thead>
                  <tr><th>Side</th><th>Contracts</th><th>Pays if right</th></tr>
                </thead>
                <tbody>
                  {mine.map((p) => (
                    <tr key={p.side}>
                      <td className={p.side === 'YES' ? 'yes' : 'no'}>{p.side}</td>
                      <td className="num">{p.qty.toLocaleString()}</td>
                      <td className="num">{credits(payoutOf(p.qty))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card">
            <h2>How this resolves</h2>
            {/*
              The criterion and source are shown in full, never truncated. They
              are the contract. A market whose rules are behind a "read more"
              link is a market whose users have not read the rules.
            */}
            <dl className="criterion">
              <dt>Resolution criterion</dt>
              <dd>{m.criterion}</dd>
              <dt>Source</dt>
              <dd>
                {m.resolution_source.startsWith('http') ? (
                  <a href={m.resolution_source} target="_blank" rel="noreferrer noopener">
                    {m.resolution_source}
                  </a>
                ) : (
                  m.resolution_source
                )}
              </dd>
              <dt>Resolves by</dt>
              <dd>{new Date(m.resolves_by).toLocaleString()}</dd>
            </dl>
            <p className="hint" style={{ marginTop: 12 }}>
              After a result is proposed there is a 24-hour window in which anyone can dispute it.
              Nothing is paid out until that window closes.
            </p>
          </div>

          <div className="card">
            <h2>Recent trades</h2>
            {tradeList.length === 0 ? (
              <div className="empty-state">No trades yet.</div>
            ) : (
              <table className="data">
                <thead>
                  <tr><th>Time</th><th>Price</th><th>Contracts</th></tr>
                </thead>
                <tbody>
                  {tradeList.slice(0, 12).map((t, i) => (
                    <tr key={i}>
                      <td>{tradeTime(t.created_at)}</td>
                      <td className="num yes">{t.price}%</td>
                      <td className="num">{t.qty.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <OrderTicket marketId={id} book={book} position={mine} disabled={ticketBlocker} onDone={refresh} />
      </div>
    </>
  );
}
